// SUMMARY: Isomorphic (server + client) encode/parse for the in-chat QUIZ card - a mixed multiple-choice +
// free-text form the document-editor assistant emits ONLY when it genuinely needs a few facts before editing.
// Same protocol as choice-form.ts: a ```evuno-quiz block (questions + a hidden state) renders as a card; on
// submit the client posts a readable "- question: answer" message PLUS a hidden ```evuno-quiz-result block,
// which the answer turn reads back (exact answers + carried state) so it can re-gather + edit statelessly.
// No server-only imports (server + client both use it).

export interface QuizChoiceQuestion {
	kind: 'choice'
	key: string
	question: string
	options: string[]
	multi: boolean
}
export interface QuizTextQuestion {
	kind: 'text'
	key: string
	question: string
	placeholder?: string
}
export type QuizQuestion = QuizChoiceQuestion | QuizTextQuestion

/** Hidden state carried across the turn so the stateless ANSWER turn re-gathers the SAME material + edits. */
export interface QuizState {
	brief: string // the ORIGINAL user instruction to fulfil after answers (drives re-gather + edit)
	useContextFiles: boolean // planner flags from the turn that produced the quiz
	readDocumentIds: string[]
}

export interface QuizPrompt {
	title: string
	questions: QuizQuestion[]
	state: QuizState
}

/** answer = human string (comma-joined labels for choice, the typed value for text). */
export interface QuizAnswer {
	key: string
	question: string
	kind: 'choice' | 'text'
	answer: string
}
export interface QuizResult {
	answers: QuizAnswer[]
	state: QuizState
}

const CARD_FENCE = 'evuno-quiz'
const RESULT_FENCE = 'evuno-quiz-result'
const CARD_BLOCK = /```evuno-quiz\s*\n([\s\S]*?)\n```/
const RESULT_BLOCK = /```evuno-quiz-result\s*\n([\s\S]*?)\n```/
const RESULT_BLOCK_G = /```evuno-quiz-result\s*\n[\s\S]*?\n```/g

// Base64 (ASCII) payload so the markdown sanitizer/renderer can never corrupt the embedded JSON.
function toBase64(text: string): string {
	return btoa(String.fromCharCode(...new TextEncoder().encode(text)))
}
function fromBase64(b64: string): string {
	return new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)))
}

const EMPTY_STATE: QuizState = { brief: '', useContextFiles: false, readDocumentIds: [] }

// ===============================
// SECTION: CARD (assistant -> user)
// ===============================

/** Encode a quiz card into an assistant message: a visible intro line + the hidden spec block. */
export function encodeQuiz(options: { intro: string; prompt: QuizPrompt }): string {
	const payload = toBase64(JSON.stringify({ version: 1, ...options.prompt }))
	return `${options.intro.trim()}\n\n\`\`\`${CARD_FENCE}\n${payload}\n\`\`\``
}

/** Decode the base64 body of an `evuno-quiz` fenced block (what the renderer receives). Null if malformed. */
export function parseQuizPayload(payload: string): QuizPrompt | null {
	try {
		const data = JSON.parse(fromBase64(payload.trim())) as Partial<QuizPrompt>
		if (!Array.isArray(data.questions)) return null
		const questions = data.questions.filter(isValidQuestion)
		if (questions.length === 0) return null
		return {
			title: typeof data.title === 'string' ? data.title : '',
			questions,
			state: normalizeState(data.state),
		}
	} catch {
		return null
	}
}

function isValidQuestion(q: unknown): q is QuizQuestion {
	if (!q || typeof q !== 'object') return false
	const question = q as Partial<QuizQuestion>
	if (typeof question.key !== 'string' || typeof question.question !== 'string' || !question.question.trim())
		return false
	if (question.kind === 'choice') return Array.isArray((question as QuizChoiceQuestion).options)
	return question.kind === 'text'
}

// ===============================
// SECTION: RESULT (user -> route)
// ===============================

/** Build the message a card posts back: a readable line + a hidden machine result the answer turn reads. */
export function encodeQuizResult(options: { humanText: string; answers: QuizAnswer[]; state: QuizState }): string {
	const payload = toBase64(JSON.stringify({ answers: options.answers, state: options.state }))
	return `${options.humanText.trim()}\n\n\`\`\`${RESULT_FENCE}\n${payload}\n\`\`\``
}

/** Parse the machine result out of an incoming user message; null if it is not a quiz answer. */
export function parseQuizResult(content: string): QuizResult | null {
	const match = RESULT_BLOCK.exec(content || '')
	if (!match) return null
	try {
		const data = JSON.parse(fromBase64(match[1].trim())) as Partial<QuizResult>
		if (!Array.isArray(data.answers)) return null
		return { answers: data.answers, state: normalizeState(data.state) }
	} catch {
		return null
	}
}

/** Remove any hidden quiz-result block so it never shows in the user's bubble. */
export function stripQuizResult(content: string): string {
	return (content || '').replace(RESULT_BLOCK_G, '').trim()
}

/** Whether a message carries a quiz CARD block (an assistant card turn). */
export function messageHasQuizCard(content: string): boolean {
	return CARD_BLOCK.test(content || '')
}

function normalizeState(state: unknown): QuizState {
	if (!state || typeof state !== 'object') return EMPTY_STATE
	const s = state as Partial<QuizState>
	return {
		brief: typeof s.brief === 'string' ? s.brief : '',
		useContextFiles: Boolean(s.useContextFiles),
		readDocumentIds: Array.isArray(s.readDocumentIds) ? s.readDocumentIds.map(String) : [],
	}
}
